import { Labels } from "../../../features/data-types"
import { round } from "../../../utils/typeUtils"
import { FiscalDocument, FiscalDocumentStatus, FiscalDocumentType } from "../state/types"

const isInvoiceOrCreditNote = (fiscalDocument: FiscalDocument): boolean =>
    fiscalDocument.documentType === FiscalDocumentType.INVOICE ||
    fiscalDocument.documentType === FiscalDocumentType.CREDIT_NOTE

const isAuthorizedInvoiceOrCreditNote = (fiscalDocument: FiscalDocument): boolean =>
    fiscalDocument.status === FiscalDocumentStatus.AUTHORIZED && isInvoiceOrCreditNote(fiscalDocument)

// Neto facturado en espacio de comprobante: facturas autorizadas menos notas de crédito autorizadas.
export const getNetInvoicedTotal = (fiscalDocuments: FiscalDocument[]): number =>
    round(fiscalDocuments.reduce((sum, fiscalDocument) => {
        if (!isAuthorizedInvoiceOrCreditNote(fiscalDocument)) {
            return sum
        }

        const total = fiscalDocument.details?.totals.total || 0
        return fiscalDocument.documentType === FiscalDocumentType.INVOICE
            ? sum + total
            : sum - total
    }, 0))

// Neto facturado en espacio de valor (con IVA, sin recargo del medio de pago): cada comprobante
// se convierte con el recargo con el que fue emitido, no con el recargo actual de la operación.
// `fallbackPaymentTypeYield` solo cubre comprobantes viejos que no guardaron su recargo.
export const getNetInvoicedHoardValue = (
    fiscalDocuments: FiscalDocument[],
    fallbackPaymentTypeYield = 0
): number =>
    round(fiscalDocuments.reduce((sum, fiscalDocument) => {
        if (!isAuthorizedInvoiceOrCreditNote(fiscalDocument)) {
            return sum
        }

        const totals = fiscalDocument.details?.totals
        const paymentTypeYield = fiscalDocument.details?.paymentTypeYield ?? fallbackPaymentTypeYield
        const factor = 1 + paymentTypeYield / 100
        if (!totals || factor <= 0) {
            return sum
        }

        const value = (totals.total - (totals.totalPerceptions || 0)) / factor
        return fiscalDocument.documentType === FiscalDocumentType.INVOICE
            ? sum + value
            : sum - value
    }, 0))

export const fiscalDocumentHelpers = {
    describeFiscalDocumentType: (fiscalDocument: Partial<FiscalDocument>): string => {
        const { documentType, documentClass } = fiscalDocument
        return documentType === FiscalDocumentType.INVOICE ?
            `${Labels.fiscalDocumentTypeLabel(documentType!)} ${documentClass!}` :
            Labels.fiscalDocumentTypeLabel(documentType!)
    },

    describeFiscalDocument: (fiscalDocument: FiscalDocument): string => {
        const type = fiscalDocumentHelpers.describeFiscalDocumentType(fiscalDocument)
        // Sin número fiscal no mostramos el sufijo colgado ("Tipo - ").
        if (!fiscalDocument.code) {
            return type
        }
        const codeParts = fiscalDocument.code.split(' ')
        const codeSuffix = codeParts[codeParts.length - 1]
        return `${type} - ${codeSuffix}`
    },

    getNetInvoicedTotal,
    getNetInvoicedHoardValue,
}
